import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'

type TransferItem = {
  id: string; productId: string; lotId: string | null; fromLocationId: string; toLocationId: string
  quantityRequested: number; quantityTransferred: number | null; breakageQty: number; breakageReason: string | null
  sku: string | null; productName: string | null; lotNumber: string | null; fromLocationCode: string | null
  palletId: string | null; palletCode: string | null
  boxesRequested: number | null; boxesTransferred: number | null; unitsPerBox: number | null
}
type Transfer = {
  id: string; tenantId: string; fromWarehouseId: string | null; toWarehouseId: string | null
  reason: string; notes: string | null; status: string; createdAt: string; completedAt: string | null
  items: TransferItem[]
}
type CompletionRow = {
  transferItemId: string; quantityTransferred: number; boxesTransferred: number
  breakageQty: number; breakageReason: string
}

const statusLabel: Record<string, string> = { pending: 'Pendente', completed: 'Concluída', cancelled: 'Cancelada' }
const statusVariant: Record<string, 'default' | 'success' | 'danger'> = { pending: 'default', completed: 'success', cancelled: 'danger' }

export function TransferDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [completions, setCompletions] = useState<Record<string, CompletionRow>>({})

  const { data: transfer, isLoading } = useQuery<Transfer>({
    queryKey: ['transfer', id],
    queryFn: async () => (await api.get(`/transfers/${id}`)).data,
  })

  useEffect(() => {
    if (!transfer) return
    setCompletions(prev => {
      // only initialize rows not yet set
      const rows: Record<string, CompletionRow> = { ...prev }
      transfer.items.forEach((item: TransferItem) => {
        if (!rows[item.id]) {
          rows[item.id] = {
            transferItemId: item.id,
            quantityTransferred: item.quantityRequested,
            boxesTransferred: item.boxesRequested ?? 0,
            breakageQty: 0,
            breakageReason: '',
          }
        }
      })
      return rows
    })
  }, [transfer])

  const complete = useMutation({
    mutationFn: () => api.post(`/transfers/${id}/complete`, { items: Object.values(completions) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfer', id] }),
  })

  const cancel = useMutation({
    mutationFn: () => api.post(`/transfers/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfer', id] }),
  })

  const setComp = (itemId: string, patch: Partial<CompletionRow>) =>
    setCompletions(p => ({ ...p, [itemId]: { ...p[itemId], ...patch } }))

  if (isLoading) return <p className="p-8 text-center text-sm text-gray-400">Carregando...</p>
  if (!transfer) return <p className="p-8 text-center text-sm text-gray-400">Transferência não encontrada.</p>

  const isPending = transfer.status === 'pending'

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/transfers')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Transferência</h2>
          <p className="text-sm text-gray-500">{transfer.reason}</p>
        </div>
        <Badge variant={statusVariant[transfer.status] ?? 'default'}>{statusLabel[transfer.status] ?? transfer.status}</Badge>
      </div>

      <Card>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 text-sm">
            <div><p className="text-gray-500">Criado em</p><p className="font-medium">{new Date(transfer.createdAt).toLocaleString('pt-BR')}</p></div>
            {transfer.completedAt && <div><p className="text-gray-500">Concluído em</p><p className="font-medium">{new Date(transfer.completedAt).toLocaleString('pt-BR')}</p></div>}
            {transfer.notes && <div><p className="text-gray-500">Obs.</p><p className="font-medium">{transfer.notes}</p></div>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Itens</span>
          {isPending && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" loading={cancel.isPending} onClick={() => cancel.mutate()}>Cancelar</Button>
              <Button size="sm" loading={complete.isPending} onClick={() => complete.mutate()}>Confirmar transferência</Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {complete.error && (
            <p className="px-5 py-3 text-sm text-red-600 border-b border-red-100 bg-red-50">
              {(complete.error as any)?.response?.data?.title ?? 'Erro ao confirmar'}
            </p>
          )}
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Produto</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Lote</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Origem</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Solicitado</th>
                {isPending && <th className="px-4 py-3 text-right font-medium text-gray-600">Transferido</th>}
                {isPending && <th className="px-4 py-3 text-right font-medium text-gray-600">Quebra</th>}
                {isPending && <th className="px-4 py-3 text-left font-medium text-gray-600">Motivo quebra</th>}
                {!isPending && <th className="px-4 py-3 text-right font-medium text-gray-600">Transferido</th>}
                {!isPending && <th className="px-4 py-3 text-right font-medium text-gray-600">Quebra</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transfer.items.map((item) => {
                const comp = completions[item.id]
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">
                      <span className="font-mono text-xs text-gray-500">{item.sku}</span><br />
                      <span>{item.productName ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-700">{item.lotNumber ?? '—'}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-700">
                      {item.palletCode ? (
                        <>
                          <span className="text-gray-900">{item.palletCode}</span><br />
                          <span className="text-xs text-gray-500">{item.fromLocationCode ?? '—'}</span>
                        </>
                      ) : (item.fromLocationCode ?? '—')}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {item.palletId ? (
                        <>
                          {item.boxesRequested ?? 0} cx
                          <span className="block text-xs font-normal text-gray-500">{item.quantityRequested} un</span>
                        </>
                      ) : item.quantityRequested}
                    </td>
                    {isPending && comp ? (
                      <>
                        <td className="px-4 py-3 text-right">
                          {item.palletId ? (
                            <>
                              <input type="number" min={0} max={item.boxesRequested ?? 0}
                                className="w-20 rounded border border-gray-200 px-2 py-1 text-sm text-right"
                                value={comp.boxesTransferred}
                                onChange={e => {
                                  const boxes = parseInt(e.target.value) || 0
                                  setComp(item.id, { boxesTransferred: boxes, quantityTransferred: boxes * (item.unitsPerBox ?? 0) })
                                }} />
                              <span className="block text-xs text-gray-500">
                                cx = {comp.boxesTransferred * (item.unitsPerBox ?? 0)} un
                              </span>
                            </>
                          ) : (
                            <input type="number" min={0} max={item.quantityRequested}
                              className="w-20 rounded border border-gray-200 px-2 py-1 text-sm text-right"
                              value={comp.quantityTransferred}
                              onChange={e => setComp(item.id, { quantityTransferred: parseInt(e.target.value) || 0 })} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input type="number" min={0}
                            className="w-16 rounded border border-gray-200 px-2 py-1 text-sm text-right"
                            value={comp.breakageQty}
                            onChange={e => setComp(item.id, { breakageQty: parseInt(e.target.value) || 0 })} />
                        </td>
                        <td className="px-4 py-3">
                          <input className="w-40 rounded border border-gray-200 px-2 py-1 text-sm"
                            placeholder="Motivo..."
                            value={comp.breakageReason}
                            onChange={e => setComp(item.id, { breakageReason: e.target.value })} />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {item.quantityTransferred == null ? '—' : item.palletId ? (
                            <>
                              {item.boxesTransferred ?? 0} cx
                              <span className="block text-xs text-gray-500">{item.quantityTransferred} un</span>
                            </>
                          ) : item.quantityTransferred}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${item.breakageQty > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                          {item.breakageQty > 0 ? item.breakageQty : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
